<script setup lang="ts">

import { cleanPath } from '~/utils'
import { useMatchFormats } from '~/composables/useMatchFormats'
import KeyLabelFeature from '~/components/KeyLabelFeature.vue'
import UiSelect from '@boats/ui/components/UiSelect.vue'
import UiTag from '@boats/ui/components/UiTag.vue'
import {useJsonLdStore} from "~/stores/jsonld";

const props = defineProps<{
  products: Array<any>
  filters: any|boolean
  title: string|boolean
  /** Query string appended to every fixture link, e.g. `?view=official-match-break` (BOAT-1912). */
  linkQuery?: string
}>()

const isClient = ref(false);

const {
  isNearDate,
  formatMatchDate,
  formatMatchTime,
  formatMatchDay,
  formatMatchTitle
} = useMatchFormats()

function onTagSelect(tag) {
  const index = props.filters.selectedTags.indexOf(tag)
  if (index === -1) {
    props.filters.selectedTags.push(tag)
  } else {
    props.filters.selectedTags.splice(index, 1)
  }
}

const isMobile = ref(false)
onMounted(() => {
  isClient.value = true;
  isMobile.value = window.innerWidth < 768
})
const getLimitedKeyFeatures = (product) => {
  return isMobile.value ? product.keyFeatureLabels.slice(0, 1) : product.keyFeatureLabels
}

const getCompetitionName = (product) => {
  return product?.competitions?.at(0)?.name
}

if (props.products && props.products.length) {
  const site = useSiteStore()
  const jsonStore = useJsonLdStore()
  props.products.forEach((item) => {
    jsonStore.addSportEvent(item, site);
  })
}

const emit = defineEmits(['onFilterChange'])
</script>

<template>
  <section class="upcoming-games events-page section_border-line">
    <div v-if="filters" class="upcoming-games__filters">
      <div class="upcoming-games__filters-scroll">
        <ul class="upcoming-games__filters-list">
          <template v-if="filters.dates.length > 0">
            <li class="upcoming-games__filters-item">
              <UiSelect
                v-model="filters.selectedDate"
                :title="$t('string.All-Dates')"
                :options="[
                      { label: $t('string.All-Dates'), value: undefined },
                      ...filters.dates.map((date) => ({
                        label: formatMatchDate(date),
                        value: date,
                      }))]"

              />
            </li>
          </template>
          <template v-if="filters.venues.length > 0">
            <li class="upcoming-games__filters-item">
              <UiSelect
                v-model="filters.selectedVenue"
                :title="$t('string.All-Locations')"
                :options="[
                        { label: $t('string.All-Locations'), value: undefined },
                        ...filters.venues.map((venue) => ({
                          label: venue.name,
                          value: venue.id,
                        }))]"
              />
            </li>
          </template>
          <li v-for="item in filters.tags" :key="item" class="upcoming-games__filters-item">
            <UiTag
              :selected="filters.selectedTags.includes(item)"
              @select="onTagSelect(item)"
            >
              {{ item.name }}
            </UiTag>
          </li>
        </ul>
      </div>
    </div>
    <div class="upcoming-games__overview">
      <h2 v-if="title" class="upcoming-games__overview-title" v-html="title"></h2>
      <ul v-if="products?.length" class="upcoming-games__overview-list">
        <li v-for="product in products" :key="product.id" class="upcoming-games__overview-item">
          <a :href="cleanPath('/', product.path) + (linkQuery || '')" class="card-link">
            <div class="upcoming-games-overview-card">
              <div class="upcoming-games-overview-card__event-time">
                <span v-if="formatMatchDate(product.startDate) !== 'TBD'"
                      :class="['upcoming-games-overview-card__event-time-info',{ 'upcoming-games-overview-card__event-time-info_near-date': isNearDate(product.startDate) }]">{{ formatMatchDay(product.startDate)
                  }}</span>
                <span class="upcoming-games-overview-card__event-time-date">{{ formatMatchDate(product.startDate)
                  }}</span>
                <span v-if="formatMatchDate(product.startDate) !== 'TBD'"
                      class="upcoming-games-overview-card__event-time-info">{{ formatMatchTime(product.startDate, product.startTime)
                  }}</span>
              </div>
              <div class="upcoming-games-overview-card__event-info">
                <div class="upcoming-games-overview-card__event-info-club-vs-club"
                     v-html="formatMatchTitle(product.title)"></div>

                <div class="flex">
                  <div class="upcoming-games-overview-card__event-info-additional-info">
                    <span v-if="getCompetitionName(product)" class="upcoming-games-competition">{{getCompetitionName(product)}}</span>
                    <span v-if="product.venue.name || product.venue.address"
                          class="upcoming-games-overview-card__event-info-text">{{ product.venue.name || product.venue.address
                      }}</span>
                    <div class="key-label-feature__content">
                      <template v-if="product.keyFeatureLabels?.length">
                        <div class="key-label-feature__list">
                          <KeyLabelFeature
                            v-for="(label, i) in getLimitedKeyFeatures(product)"
                            :key="'key-label-feature' + i + product.id"
                            :name="label.name"
                            :colour="label.colour"
                          />
                        </div>
                      </template>
                    </div>
                  </div>
                  <div class="upcoming-games-overview-card__event-tickets-info only-desktop">
                    <div class="upcoming-games-overview-card__event-tickets-info-tickets-button">
                      <span> see tickets</span>
                      <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clip-path="url(#clip0_5680_1853)">
                          <path d="M10.0698 3.63843L16.2586 10.0045L10.0698 16.3706" stroke="#121318"
                                stroke-width="1.5" stroke-miterlimit="10" />
                          <path d="M15.7 10L2.5 10" stroke="#121318" stroke-width="1.5" stroke-miterlimit="10" />
                        </g>
                        <defs>
                          <clipPath id="clip0_5680_1853">
                            <rect width="15" height="14" fill="white" transform="translate(2.5 3)" />
                          </clipPath>
                        </defs>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
              <div class="upcoming-games-overview-card__event-tickets-info only-mobile">
                <div class="upcoming-games-overview-card__event-tickets-info-tickets-button">
                  <span> see tickets</span>
                  <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clip-path="url(#clip0_5680_1853)">
                      <path d="M10.0698 3.63843L16.2586 10.0045L10.0698 16.3706" stroke="#121318"
                            stroke-width="1.5" stroke-miterlimit="10" />
                      <path d="M15.7 10L2.5 10" stroke="#121318" stroke-width="1.5" stroke-miterlimit="10" />
                    </g>
                    <defs>
                      <clipPath id="clip0_5680_1853">
                        <rect width="15" height="14" fill="white" transform="translate(2.5 3)" />
                      </clipPath>
                    </defs>
                  </svg>
                </div>
              </div>
              <svg class="upcoming-games-overview-card__event-tickets-info-tickets-chevron" width="9" height="14"
                   viewBox="0 0 9 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0.54834 0.599976L6.96532 6.99998L0.54834 13.4" stroke="#5A5A5A" stroke-width="1.5"
                      stroke-miterlimit="10" />
              </svg>
            </div>
          </a>
        </li>
      </ul>
    </div>
  </section>
</template>

<style lang="scss">
@import '@boats/ui/styles/config';

.key-label-feature__content {
  min-height: unset;
  display: grid;
  flex: 1 1 0;

  @media (max-width: 599px) {
    align-self: flex-start;
  }
  @include media-breakpoint-up(lg) {
    max-width: 100%;
    min-height: 32px;
  }
}

.key-label-feature__list {
  scrollbar-width: none;
  overflow-x: unset;
  text-align: end;
  white-space: nowrap;
  gap: 8px;
  justify-content: flex-end;
  @include media-breakpoint-up(lg) {
    justify-content: flex-start;
    text-align: start;
    overflow-x: auto;
  }
}

.upcoming-games-competition {
  margin-top: -10px;
  font-family: "Barlow";
  font-weight: 500;
  font-size: 12px;
  color: #5a5a5a;
  width: 100%;

  @include media-breakpoint-up(lg) {
    font-size: 16px;
  }
}

.upcoming-games {
  a {
    text-decoration: none;
  }
  ul, li {
    list-style-type: none;
  }

  padding: 14px 0 40px 0;

  @include media-breakpoint-up(lg) {
    padding: 62px 0;
  }

  &.events-page {
    @include media-breakpoint-up(lg) {
      padding-top: 24px;
      padding-bottom: 0;
    }
  }


  &__overview {
    &-title {
      @include barlow-condensed-26-32-uppercase;
      width: 100%;
      margin-bottom: 26px;
    }

    &-list {
      display: grid;
      grid-template-columns: 1fr;
      grid-row-gap: 8px;
      list-style-type: none;
      padding-left: 0;

      @include media-breakpoint-up(lg) {
        grid-row-gap: 0;
      }
    }

    &-item {
      @include media-breakpoint-up(lg) {
        border-top: 1px solid $gray-200;

        &:last-child {
          border-bottom: 1px solid $gray-200;
        }
      }
    }
  }

  &__filters {
    z-index: 1;
    position: relative;
    margin-bottom: 24px;
    max-width: 100%;
    box-sizing: border-box;

    @include media-breakpoint-up(lg) {
      margin-bottom: 32px;
    }

    &-scroll {
      display: grid;
    }

    &-list {
      list-style-type: none;
      padding-left: 0;
      overflow-x: auto;
      scrollbar-width: none;
      display: flex;
      flex-wrap: nowrap;
      gap: 12px;

      &::-webkit-scrollbar {
        display: none;
      }

      @include media-breakpoint-up(lg) {
        gap: 24px;
      }
    }
  }

  &-overview-card {
    position: relative;
    display: flex;
    border-radius: 4px;
    padding: 12px 12px 14px 14px;
    box-shadow: 0px 0px 12px 0px #00000014;
    cursor: pointer;

    &:hover {
      cursor: pointer;
      @include smooth-transition;

      @include media-breakpoint-up(lg) {
        background-color: $white;
        box-shadow: 2px 10px 32px 0px #00000014;
        transform: scale(1.01);

        & .upcoming-games-overview-card__event-tickets-info-tickets-button {
          transition: all 300ms ease;
          background-color: $red;
          border: transparent;
          color: $white;

          & svg path {
            stroke: $white;
          }
        }
      }
    }

    @include media-breakpoint-up(lg) {
      justify-content: space-between;
      padding: 22px;
      box-shadow: none;
      border-radius: 0;
      cursor: default;
      gap: 24px;
      left: -22px;
      width: calc(100% + 22px);
    }

    &_link {
      @include media-breakpoint-up(lg) {
        & {
          pointer-events: none;
        }
        & button, a {
          pointer-events: auto;
        }
      }
    }

    &__event-time {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding-right: 12px;
      gap: 6px;
      border-radius: 4px;
      border-right: 1px solid $gray-100;
      min-width: 90px;

      @include media-breakpoint-up(lg) {
        width: 121px;
        padding-right: 0;
        background-color: $gray-100;
      }

      &-info {
        @include label-tag;
        display: block;
        color: $black;

        &_near-date {
          padding: 0 8px;
          border-radius: 90px;
          background-color: #9ECDF9;
        }
      }

      &-date {
        display: block;
        @include barlow-condensed-20-24-uppercase;
      }
    }

    &__event-info {
      display: flex;
      width: 100%;
      flex-direction: column;
      flex: 1;
      padding-left: 12px;
      gap: 6px;

      @include media-breakpoint-up(lg) {
        gap: 9px;
        padding: 0;
      }

      &-club-vs-club {
        display: flex;
        flex-direction: column;
        align-items: start;
        gap: 6px;

        @include media-breakpoint-up(xl) {
          flex-direction: row;
          align-items: center;
          gap: 4px;
        }

        & span {
          @include h4-poppins;
        }

        & img {
          display: block;
        }

        & > div {
          display: flex;
          align-items: center;
          gap: 8px;

          @include media-breakpoint-down(xl) {
            &:first-of-type {
              & > span {
                order: 1;
              }
            }
          }
        }

        & > .vs {
          display: none;

          @include media-breakpoint-up(xl) {
            display: block;
            width: 32px;
            text-align: center;
            font-family: 'Poppins';
            font-style: normal;
            font-weight: 500;
            font-size: 16px;
            line-height: 32px;
            color: $black;
          }
        }
      }

      &-additional-info {
        display: flex;
        row-gap: 1px;
        column-gap: 9px;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        flex-wrap: wrap;
        width: 100%;

        @include media-breakpoint-up(lg) {
          align-items: flex-start;
          flex-direction: column;
          width: calc(100% - 160px);
        }

        @media (max-width: 599px) {
          flex-direction: column;
        }
      }

      &-text {
        @include barlow-14-16-grey-400;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
        width: calc(100vw - 90px - 48px - 1.5rem);

        @include media-breakpoint-up(lg) {
          white-space: wrap;
          width: 100%;
        }

        @media (max-width: 599px) {
          flex: auto;
          align-self: flex-start;
        }
      }
    }

    &__event-tickets-info {
      display: none;
      align-items: center;

      @include media-breakpoint-up(lg) {
        display: flex;
        width: 160px;
        align-items: end;
      }

      &-tickets-chevron {
        position: absolute;
        right: 9px;
        top: 50%;
        transform: translateY(-50%);
        width: 16px;
        height: 16px;

        @include media-breakpoint-up(lg) {
          display: none;
        }
      }

      &-tickets-button {
        @include label-button;
        display: none;
        gap: 7px;
        align-items: center;
        outline: none;
        border: 1px solid $black;
        border-radius: 2px;
        text-transform: uppercase;
        padding: 15.5px 16px;
        cursor: pointer;
        color: $black;
        background-color: $white;

        @include media-breakpoint-up(lg) {
          display: flex;
        }

        & > svg {
          width: 20px;
          height: 20px;
        }
      }
    }
  }
}

.related-matches {
  position: relative;

  &__title {
    margin-bottom: 27px;
  }

  &__slider {
    &-container {
      min-width: 380px;
      overflow-x: scroll;
      &::-webkit-scrollbar {
        display: none;
      }

      @include media-breakpoint-up(lg) {
        overflow-x: hidden;
      }
    }

    &-nav {
      display: none;
      position: absolute;
      left: 0;
      bottom: 77px;
      align-items: center;
      background-color: transparent;
      gap: 8px;

      @include media-breakpoint-up(lg) {
        display: flex;
      }
    }

    &-pagination {
      @include label-secondary-nav;
    }
  }

  &__list {
    display: flex;
    gap: 12px;
    transition: all 300ms ease;

    @include media-breakpoint-up(lg) {
      gap: 40px;
    }
  }

  &-card {
    width: 120px;

    @include media-breakpoint-up(lg) {
      width: 180px;
    }

    &__image {
      position: relative;
      height: 180px;
      border-radius: 4px;
      margin-bottom: 12px;
      overflow: hidden;
      background-repeat: no-repeat;
      background-size: contain;
      background-color: $gray-200;

      @include media-breakpoint-up(lg) {
        border-radius: 6px;
        height: 270px;
      }
    }

    &__button {
      @include no-bg-button;
      display: none;
      position: absolute;
      top: 11px;
      right: 13px;
      z-index: 2;
      padding: 0;
      width: 32px;
      height: 32px;

      & > svg {
        width: 32px;
        height: 32px;
      }

      @include media-breakpoint-up(lg) {
        display: block;
      }
    }

    &__overlay {
      position: absolute;
      width: 100%;
      height: 100%;
      background-color: rgba(0,0,0, 0.29);
      z-index: 1;
    }

    &__clubs-symbol {
      position: absolute;
      left: 50%;
      top: 50%;
      z-index: 2;
      transform: translate(-50%, -50%);
      width: 80px;
      height: 94px;
      border: none;

      @include media-breakpoint-up(lg) {
        width: 85px;
        height: 100px;
      }
    }

    &__title {
      @include h4-poppins;
    }
  }
}

</style>
